import { useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";

// Canonical sections + safe rename aliases (mirror backend resume_serializer).
export const CANON = ["SUMMARY", "EXPERIENCE", "EDUCATION", "SKILLS", "TECHNICAL SKILLS", "PROJECTS", "CERTIFICATIONS"];
export const ALIASES = {
  SUMMARY: ["Summary", "Professional Summary", "Profile", "Career Objective"],
  EXPERIENCE: ["Experience", "Work Experience", "Professional Experience", "Employment"],
  EDUCATION: ["Education", "Academic Background"],
  SKILLS: ["Skills", "Core Competencies", "Key Skills"],
  "TECHNICAL SKILLS": ["Technical Skills", "Technologies", "Tech Stack"],
  PROJECTS: ["Projects", "Key Projects", "Portfolio"],
  CERTIFICATIONS: ["Certifications", "Licenses & Certifications", "Professional Development"],
};
export const ACCENTS = ["#2832C2", "#0F766E", "#1E3A8A", "#9333EA", "#B91C1C", "#0A1128", "#C2410C"];
export const FONTS = [
  { id: "Helvetica", label: "Helvetica (sans)" },
  { id: "Times", label: "Times (serif)" },
  { id: "Courier", label: "Courier (mono)" },
];

const _has = (s) => Array.isArray(s) ? s.length > 0 : !!(s && String(s).trim());

// Which canonical sections currently hold content.
function populated(s) {
  return {
    SUMMARY: _has(s.summary), EXPERIENCE: _has(s.experience), EDUCATION: _has(s.education),
    SKILLS: _has(s.skills), "TECHNICAL SKILLS": _has(s.technical_skills),
    PROJECTS: _has(s.projects), CERTIFICATIONS: _has(s.certifications),
  };
}

// structured -> {name, contact, sections:[{title,content[]}]} for the preview templates.
export function structuredToPreview(s) {
  const labelOf = (c) => (s.section_label && s.section_label[c]) || c;
  const order = (s.section_order && s.section_order.length ? s.section_order : CANON).filter((c) => CANON.includes(c));
  const contactBits = [s.contact?.phone, s.contact?.email, s.contact?.location, ...(s.contact?.links || [])].filter(Boolean);
  const sections = [];
  const push = (title, content) => content.length && sections.push({ title, content });
  for (const c of order) {
    if (c === "SUMMARY" && _has(s.summary)) push(labelOf(c), [s.summary]);
    if (c === "EXPERIENCE") (s.experience || []).forEach((e) => {
      const head = [e.company, e.location, e.title].filter(Boolean).join(" | ");
      const lines = []; if (head) lines.push(head); if (e.dates) lines.push(e.dates);
      (e.bullets || []).forEach((b) => b && lines.push("• " + b));
      if (lines.length && !sections.find((x) => x.title === labelOf(c))) push(labelOf(c), lines);
      else if (lines.length) sections[sections.findIndex((x) => x.title === labelOf(c))].content.push(...lines);
    });
    if (c === "EDUCATION") (s.education || []).forEach((e) => {
      const head = [e.institution, e.location, e.degree].filter(Boolean).join(" | ");
      const lines = []; if (head) lines.push(head); if (e.dates) lines.push(e.dates); if (e.details) lines.push(e.details);
      if (lines.length) { const i = sections.findIndex((x) => x.title === labelOf(c)); i >= 0 ? sections[i].content.push(...lines) : push(labelOf(c), lines); }
    });
    if (c === "SKILLS" && _has(s.skills)) push(labelOf(c), [(s.skills || []).join(" • ")]);
    if (c === "TECHNICAL SKILLS" && _has(s.technical_skills)) push(labelOf(c), [(s.technical_skills || []).join(" • ")]);
    if (c === "PROJECTS") (s.projects || []).forEach((p) => {
      const head = [p.name, p.stack].filter(Boolean).join(" | ");
      const lines = []; if (head) lines.push(head); (p.bullets || []).forEach((b) => b && lines.push("• " + b));
      if (lines.length) { const i = sections.findIndex((x) => x.title === labelOf(c)); i >= 0 ? sections[i].content.push(...lines) : push(labelOf(c), lines); }
    });
    if (c === "CERTIFICATIONS" && _has(s.certifications)) push(labelOf(c), (s.certifications || []).map((x) => "• " + x));
  }
  return { name: s.name || "Your Name", title: (s.title || "").trim(), contact: contactBits.join(" | "), sections };
}

// Canonical header detection mirroring the backend resume_parser synonyms, so the
// editor/preview segments real-world headers ("PROFESSIONAL EXPERIENCE",
// "EDUCATION & CERTIFICATIONS", letter-spaced "EDUCA TION") — not just exact CANON.
const _HEADER_SYNONYMS = {
  SUMMARY: ["summary", "professional summary", "career summary", "executive summary", "summary of qualifications", "profile", "professional profile", "career profile", "objective", "career objective", "professional objective", "about", "about me", "overview", "professional overview", "highlights"],
  EXPERIENCE: ["experience", "professional experience", "work experience", "relevant experience", "employment history", "work history", "career history", "employment", "professional background", "industry experience"],
  EDUCATION: ["education", "educational qualifications", "academic qualifications", "academic background", "education and training", "academics"],
  "TECHNICAL SKILLS": ["technical skills", "tech stack", "technologies", "technology", "tools and technologies", "skill set", "skillset"],
  SKILLS: ["skills", "key skills", "core competencies", "areas of expertise", "professional skills", "soft skills", "competencies", "proficiencies"],
  PROJECTS: ["projects", "personal projects", "academic projects", "key projects", "notable projects", "selected projects", "portfolio"],
  CERTIFICATIONS: ["certifications", "certification", "certificates", "licenses and certifications", "licenses & certifications", "licenses", "accreditations", "professional development", "courses", "training"],
};
const _HMAP = (() => {
  const m = {};
  for (const canon in _HEADER_SYNONYMS)
    for (const v of _HEADER_SYNONYMS[canon])
      m[v.toUpperCase().replace(/[^A-Z& ]/g, "").replace(/\s+/g, " ").trim()] = canon;
  return m;
})();
// Returns the canonical section for a header-ish line, else null. Tolerant of
// letter-spacing and combined "&"/"and" headers; guarded to short lines + EXACT
// (de-spaced) matches so sentences never misfire.
function _matchHeader(line) {
  if (!line || line.length > 50) return null;
  const n = line.toUpperCase().replace(/[^A-Z& ]/g, "").replace(/\s+/g, " ").trim();
  if (!n) return null;
  if (_HMAP[n]) return _HMAP[n];
  const compact = n.replace(/ /g, "");
  for (const k in _HMAP) if (k.replace(/ /g, "") === compact) return _HMAP[k];
  if (/&| AND /.test(n)) {
    for (const seg of n.split(/\s*&\s*|\s+AND\s+/)) {
      const sc = seg.replace(/ /g, "").trim();
      if (sc) for (const k in _HMAP) if (k.replace(/ /g, "") === sc) return _HMAP[k];
    }
  }
  return null;
}
const _BULLET_RE = /^\s*[•\-*▪◦·●○◆■▸‣o]\s+/;  // real bullet glyphs incl. ● (U+25CF)
const _DIVIDER_RE = /^[_\-–—=.\s]{4,}$/;          // "______", "----" separator lines

// Best-effort parse of resume text -> structured (seeds the editor + preview for
// resumes without a stored structured object — i.e. the free / legacy flow).
export function parseToStructured(text) {
  const s = { name: "", title: "", contact: { email: "", phone: "", location: "", links: [] },
    summary: "", experience: [], education: [], skills: [], technical_skills: [], projects: [], certifications: [] };
  const nonEmpty = (text || "").split("\n").map((l) => l.trim()).filter((l) => l && !_DIVIDER_RE.test(l));
  if (!nonEmpty.length) return s;
  s.name = nonEmpty[0];
  const isBullet = (l) => _BULLET_RE.test(l);
  const strip = (l) => l.replace(_BULLET_RE, "").trim();
  // contact + title before the first recognised header
  let i = 1;
  for (; i < nonEmpty.length && !_matchHeader(nonEmpty[i]); i++) {
    const l = nonEmpty[i];
    if (/@|linkedin|github|https?:|\d{5,}/i.test(l)) {
      l.split(/[|·]/).map((x) => x.trim()).forEach((p) => {
        if (/@/.test(p)) s.contact.email = p;
        else if (/linkedin|github|https?:/i.test(p)) s.contact.links.push(p);
        else if (/\d{5,}/.test(p)) s.contact.phone = p;
        else if (p) s.contact.location = p;
      });
    } else if (!s.title) s.title = l;
  }
  let cur = null;
  for (; i < nonEmpty.length; i++) {
    const l = nonEmpty[i];
    const h = _matchHeader(l);
    if (h) { cur = h; continue; }
    if (cur === "SUMMARY") s.summary = (s.summary ? s.summary + " " : "") + l;
    else if (cur === "SKILLS") s.skills.push(...l.split(/[•·|,]/).map((x) => strip(x).trim()).filter(Boolean));
    else if (cur === "TECHNICAL SKILLS") s.technical_skills.push(...l.split(/[•·|,]/).map((x) => strip(x).trim()).filter(Boolean));
    else if (cur === "CERTIFICATIONS") s.certifications.push(strip(l));
    else if (cur === "EXPERIENCE") {
      const last = s.experience[s.experience.length - 1];
      if (isBullet(l)) { if (last) last.bullets.push(strip(l)); else s.experience.push({ company: "", title: "", location: "", dates: "", bullets: [strip(l)] }); }
      // a wrapped bullet continuation (lowercase / "(" start) — merge into the
      // previous bullet instead of starting a bogus new company entry.
      else if (last && last.bullets.length && /^[a-z(]/.test(l)) last.bullets[last.bullets.length - 1] += " " + l;
      else if (/\b(19|20)\d{2}\b/.test(l) && last && !last.dates && !last.bullets.length) last.dates = l;
      else if (last && last.company && !last.title && !last.dates && !last.bullets.length && !/\|/.test(l)) last.title = l;
      else { const [company, location, title] = l.split("|").map((x) => x.trim()); s.experience.push({ company: company || l, location: location || "", title: title || "", dates: "", bullets: [] }); }
    } else if (cur === "EDUCATION") {
      const last = s.education[s.education.length - 1];
      if (/\b(19|20)\d{2}\b/.test(l) && last && !last.dates) last.dates = l;
      else if (last && last.institution && !last.details && !l.includes("|") && !/\b(19|20)\d{2}\b/.test(l)) last.details = l;
      else { const [institution, location, degree] = l.split("|").map((x) => x.trim()); s.education.push({ institution: institution || l, location: location || "", degree: degree || "", dates: "", details: "" }); }
    } else if (cur === "PROJECTS") {
      const last = s.projects[s.projects.length - 1];
      if (isBullet(l)) { if (last) last.bullets.push(strip(l)); }
      else { const [name, stack] = l.split("|").map((x) => x.trim()); s.projects.push({ name: name || l, stack: stack || "", bullets: [] }); }
    }
  }
  return s;
}

// ── small field primitives ──────────────────────────────────────────────────
const inputCls = "w-full px-2.5 py-1.5 rounded-md border border-[#e5e7eb] text-sm text-[#0A1128] focus:outline-none focus:border-[#2832C2]";
const Field = ({ v, onChange, placeholder, label }) => (
  <div>
    {label && <label className="block text-[10px] font-semibold uppercase tracking-wider text-[var(--ap-muted)] mb-1">{label}</label>}
    <input className={inputCls} value={v || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
  </div>
);

function BulletList({ bullets, onChange }) {
  return (
    <div className="space-y-1.5 mt-1.5">
      {(bullets || []).map((b, i) => (
        <div key={i} className="flex gap-1.5 items-start">
          <span className="text-[#2832C2] mt-2 text-xs">•</span>
          <textarea className={inputCls + " resize-y min-h-[34px]"} value={b}
            onChange={(e) => { const n = [...bullets]; n[i] = e.target.value; onChange(n); }} />
          <button type="button" onClick={() => onChange(bullets.filter((_, j) => j !== i))} className="text-gray-300 hover:text-red-500 mt-1.5 bg-transparent border-0 cursor-pointer" title="Remove bullet"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...(bullets || []), ""])} className="text-xs text-[#2832C2] hover:underline bg-transparent border-0 cursor-pointer inline-flex items-center gap-1"><Plus className="w-3 h-3" /> bullet</button>
    </div>
  );
}

function TagList({ items, onChange, placeholder }) {
  const [input, setInput] = useState("");
  const add = () => { const v = input.trim(); if (v) { onChange([...(items || []), v]); setInput(""); } };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {(items || []).map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-[#2832C2]/10 text-[#2832C2] text-xs px-2 py-1 rounded-full">
            {it}<button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="hover:text-red-500 bg-transparent border-0 cursor-pointer">×</button>
          </span>
        ))}
      </div>
      <input className={inputCls} value={input} placeholder={placeholder} onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} onBlur={add} />
    </div>
  );
}

// Auto-detect the platform of a link so each chip gets a matching icon + colour.
function linkMeta(url) {
  const u = String(url || "").toLowerCase();
  if (u.includes("linkedin.")) return { label: "LinkedIn", color: "#0A66C2", kind: "linkedin" };
  if (u.includes("github.")) return { label: "GitHub", color: "#24292F", kind: "github" };
  if (u.includes("gitlab.")) return { label: "GitLab", color: "#FC6D26", kind: "web" };
  if (u.includes("twitter.") || u.includes("://x.com") || u.includes(" x.com")) return { label: "X", color: "#111111", kind: "web" };
  if (u.includes("behance.") || u.includes("dribbble.")) return { label: "Portfolio", color: "#EA4C89", kind: "web" };
  if (u.includes("medium.") || u.includes("dev.to") || u.includes("hashnode.") || u.includes("substack.")) return { label: "Blog", color: "#00AB6C", kind: "web" };
  if (u.includes("leetcode.") || u.includes("hackerrank.") || u.includes("kaggle.") || u.includes("codeforces.")) return { label: "Profile", color: "#F59E0B", kind: "web" };
  return { label: "Website", color: "#6B7280", kind: "web" };
}
const shortLink = (url) => String(url || "").replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/$/, "");

// Small inline brand/generic marks — no icon-library dependency.
function LinkIcon({ kind }) {
  if (kind === "linkedin")
    return <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 17.34V10.2H6.06v7.14h2.28zM7.2 9.24a1.32 1.32 0 1 0 0-2.64 1.32 1.32 0 0 0 0 2.64zM18 17.34v-3.92c0-2.1-1.12-3.08-2.62-3.08-1.2 0-1.74.66-2.04 1.12v-.96h-2.28c.03.64 0 7.14 0 7.14h2.28v-3.99c0-.2.01-.4.07-.55.16-.4.53-.82 1.14-.82.8 0 1.13.61 1.13 1.51v3.85H18z"/></svg>;
  if (kind === "github")
    return <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M12 2C6.48 2 2 6.48 2 12c0 4.42 2.87 8.17 6.84 9.5.5.09.68-.22.68-.48v-1.7c-2.78.6-3.37-1.34-3.37-1.34-.45-1.16-1.11-1.47-1.11-1.47-.9-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.9 1.52 2.34 1.08 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.65 0 0 .84-.27 2.75 1.02a9.6 9.6 0 0 1 5 0c1.9-1.29 2.75-1.02 2.75-1.02.55 1.38.2 2.4.1 2.65.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.68-4.57 4.93.36.31.68.92.68 1.85v2.74c0 .27.18.58.69.48A10 10 0 0 0 22 12c0-5.52-4.48-10-10-10z"/></svg>;
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3c2.5 2.5 2.5 15.5 0 18M12 3c-2.5 2.5-2.5 15.5 0 18"/></svg>;
}

// Multi-link editor: each link is a chip with an auto-detected platform icon.
// Add as many as you like (comma or Enter commits; paste comma-separated too).
function LinksList({ items, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const parts = input.split(",").map((x) => x.trim()).filter(Boolean);
    if (parts.length) { onChange([...(items || []), ...parts]); setInput(""); }
  };
  return (
    <div>
      {(items || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {(items || []).map((it, i) => {
            const m = linkMeta(it);
            return (
              <span key={i} title={it} className="inline-flex items-center gap-1.5 text-xs pl-2 pr-1.5 py-1 rounded-full max-w-full" style={{ background: `${m.color}14`, color: m.color }}>
                <LinkIcon kind={m.kind} />
                <span className="max-w-[150px] truncate">{shortLink(it)}</span>
                <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="hover:opacity-60 bg-transparent border-0 cursor-pointer leading-none text-sm" aria-label={`Remove ${m.label} link`}>×</button>
              </span>
            );
          })}
        </div>
      )}
      <input className={inputCls} value={input} placeholder="Paste a link — linkedin.com/in/you, github.com/you, your site…"
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }} onBlur={add} />
    </div>
  );
}

// ── main editor ─────────────────────────────────────────────────────────────
export default function ResumeEditor({ value, design, onChange, onDesignChange }) {
  const s = value;
  const [collapsed, setCollapsed] = useState({});
  const toggle = (c) => setCollapsed((p) => ({ ...p, [c]: !p[c] }));
  const set = (patch) => onChange({ ...s, ...patch });
  const setContact = (patch) => set({ contact: { ...(s.contact || {}), ...patch } });
  const pop = populated(s);
  const order = (s.section_order && s.section_order.length ? s.section_order : CANON).filter((c) => CANON.includes(c));
  const visible = order.filter((c) => pop[c]);
  const hidden = CANON.filter((c) => !pop[c]);

  const move = (c, dir) => {
    const ord = [...visible]; const i = ord.indexOf(c); const j = i + dir;
    if (j < 0 || j >= ord.length) return;
    [ord[i], ord[j]] = [ord[j], ord[i]];
    // rebuild full order: moved-visible first, then any non-visible canon
    const full = [...ord, ...CANON.filter((x) => !ord.includes(x))];
    set({ section_order: full });
  };
  const rename = (c, label) => set({ section_label: { ...(s.section_label || {}), [c]: label } });
  const labelFor = (c) => (s.section_label && s.section_label[c]) || ALIASES[c][0];

  // IMPORTANT: this is a render FUNCTION, not an inline component. Defining a
  // component inside render gives it a new identity every render, which makes
  // React remount the whole subtree on each keystroke/click — that was the
  // "clicking + jumps to top / loses focus" bug. Calling a function inlines the
  // elements, so reconciliation by key is stable.
  const renderShell = (c, body) => {
    const open = !collapsed[c];
    return (
      <div key={c} className="bg-white border border-[#e5e7eb] rounded-xl overflow-hidden">
        <div className="flex items-center gap-1.5 px-3 py-2.5 hover:bg-[#fafbff]">
          <button type="button" onClick={() => toggle(c)} className="text-gray-400 hover:text-[#2832C2] bg-transparent border-0 cursor-pointer" title={open ? "Collapse" : "Expand"}>
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
          </button>
          <select value={labelFor(c)} onChange={(e) => rename(c, e.target.value)}
            className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#2832C2] bg-transparent border-0 cursor-pointer focus:outline-none"
            title="Rename section">
            {ALIASES[c].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <div className="ml-auto flex items-center gap-0.5">
            <button type="button" onClick={() => move(c, -1)} className="text-gray-300 hover:text-[#2832C2] bg-transparent border-0 cursor-pointer p-0.5" title="Move up"><ChevronUp className="w-4 h-4" /></button>
            <button type="button" onClick={() => move(c, 1)} className="text-gray-300 hover:text-[#2832C2] bg-transparent border-0 cursor-pointer p-0.5" title="Move down"><ChevronDown className="w-4 h-4" /></button>
          </div>
        </div>
        {open && <div className="px-4 pb-4">{body}</div>}
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="resume-editor">
      {/* Design panel */}
      <div className="bg-gradient-to-br from-[#2832C2]/[0.06] to-[#FF6B35]/[0.04] border border-[#2832C2]/15 rounded-2xl p-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#2832C2] mb-3">Design</p>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 w-12 shrink-0">Accent</span>
            <div className="flex items-center gap-2 flex-wrap">
              {ACCENTS.map((a) => (
                <button key={a} type="button" onClick={() => onDesignChange({ ...design, accent: a })}
                  className={`w-7 h-7 rounded-full cursor-pointer transition-transform hover:scale-110 ${design.accent === a ? "ring-2 ring-offset-2 ring-[#0A1128]" : ""}`}
                  style={{ background: a }} title={a} aria-label={`accent ${a}`} />
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 w-12 shrink-0">Font</span>
            <div className="inline-flex rounded-lg border border-[#e5e7eb] bg-white p-0.5">
              {FONTS.map((f) => (
                <button key={f.id} type="button" onClick={() => onDesignChange({ ...design, font: f.id })}
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors cursor-pointer ${design.font === f.id ? "bg-[#2832C2] text-white font-semibold" : "text-gray-600 hover:bg-gray-50"}`}>
                  {f.label.split(" ")[0]}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white border border-[#e5e7eb] rounded-xl p-4 space-y-2.5">
        <Field label="Full name" v={s.name} onChange={(v) => set({ name: v })} placeholder="e.g. Daniel Diaz" />
        <Field label="Professional title" v={s.title} onChange={(v) => set({ title: v })} placeholder="e.g. Backend Developer" />
        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Email" v={s.contact?.email} onChange={(v) => setContact({ email: v })} placeholder="you@email.com" />
          <Field label="Phone" v={s.contact?.phone} onChange={(v) => setContact({ phone: v })} placeholder="+1 555 000 0000" />
        </div>
        <Field label="Location" v={s.contact?.location} onChange={(v) => setContact({ location: v })} placeholder="City, Country" />
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-[var(--ap-muted)] mb-1">Links <span className="normal-case text-[var(--ap-muted)]/70">— add as many as you like</span></label>
          <LinksList items={s.contact?.links || []} onChange={(links) => setContact({ links })} />
        </div>
      </div>

      {/* Sections in order — renderShell is called as a function (not <Component/>)
          so the section subtree is NOT remounted on every edit (keeps scroll + focus). */}
      {visible.map((c) => renderShell(c, (
        <>
          {c === "SUMMARY" && <textarea className={inputCls + " resize-y min-h-[64px]"} value={s.summary || ""} onChange={(e) => set({ summary: e.target.value })} placeholder="2-3 sentence summary" />}
          {c === "SKILLS" && <TagList items={s.skills} onChange={(v) => set({ skills: v })} placeholder="Add a skill, Enter" />}
          {c === "TECHNICAL SKILLS" && <TagList items={s.technical_skills} onChange={(v) => set({ technical_skills: v })} placeholder="Add a tech, Enter" />}
          {c === "CERTIFICATIONS" && <TagList items={s.certifications} onChange={(v) => set({ certifications: v })} placeholder="Cert | Issuer | Year, Enter" />}
          {c === "EXPERIENCE" && (
            <div className="space-y-3">
              {(s.experience || []).map((e, i) => (
                <div key={i} className="border-l-2 border-[#2832C2]/15 pl-3">
                  <div className="grid grid-cols-2 gap-1.5">
                    <Field v={e.company} onChange={(v) => { const n = [...s.experience]; n[i] = { ...e, company: v }; set({ experience: n }); }} placeholder="Company" />
                    <Field v={e.title} onChange={(v) => { const n = [...s.experience]; n[i] = { ...e, title: v }; set({ experience: n }); }} placeholder="Title" />
                    <Field v={e.location} onChange={(v) => { const n = [...s.experience]; n[i] = { ...e, location: v }; set({ experience: n }); }} placeholder="Location" />
                    <Field v={e.dates} onChange={(v) => { const n = [...s.experience]; n[i] = { ...e, dates: v }; set({ experience: n }); }} placeholder="Jan 2022 - Present" />
                  </div>
                  <BulletList bullets={e.bullets} onChange={(b) => { const n = [...s.experience]; n[i] = { ...e, bullets: b }; set({ experience: n }); }} />
                  <button type="button" onClick={() => set({ experience: s.experience.filter((_, j) => j !== i) })} className="text-xs text-red-400 hover:text-red-600 bg-transparent border-0 cursor-pointer mt-1">Remove role</button>
                </div>
              ))}
              <button type="button" onClick={() => set({ experience: [...(s.experience || []), { company: "", title: "", location: "", dates: "", bullets: [] }] })} className="text-xs text-[#2832C2] hover:underline bg-transparent border-0 cursor-pointer inline-flex items-center gap-1"><Plus className="w-3 h-3" /> role</button>
            </div>
          )}
          {c === "EDUCATION" && (
            <div className="space-y-2">
              {(s.education || []).map((e, i) => (
                <div key={i} className="grid grid-cols-2 gap-1.5 border-l-2 border-[#2832C2]/15 pl-3">
                  <Field v={e.institution} onChange={(v) => { const n = [...s.education]; n[i] = { ...e, institution: v }; set({ education: n }); }} placeholder="Institution" />
                  <Field v={e.degree} onChange={(v) => { const n = [...s.education]; n[i] = { ...e, degree: v }; set({ education: n }); }} placeholder="Degree" />
                  <Field v={e.dates} onChange={(v) => { const n = [...s.education]; n[i] = { ...e, dates: v }; set({ education: n }); }} placeholder="2017 - 2021" />
                  <Field v={e.details} onChange={(v) => { const n = [...s.education]; n[i] = { ...e, details: v }; set({ education: n }); }} placeholder="CGPA / honors" />
                  <button type="button" onClick={() => set({ education: s.education.filter((_, j) => j !== i) })} className="text-xs text-red-400 hover:text-red-600 bg-transparent border-0 cursor-pointer col-span-2 text-left">Remove</button>
                </div>
              ))}
              <button type="button" onClick={() => set({ education: [...(s.education || []), { institution: "", degree: "", location: "", dates: "", details: "" }] })} className="text-xs text-[#2832C2] hover:underline bg-transparent border-0 cursor-pointer inline-flex items-center gap-1"><Plus className="w-3 h-3" /> education</button>
            </div>
          )}
          {c === "PROJECTS" && (
            <div className="space-y-3">
              {(s.projects || []).map((p, i) => (
                <div key={i} className="border-l-2 border-[#2832C2]/15 pl-3">
                  <div className="grid grid-cols-2 gap-1.5">
                    <Field v={p.name} onChange={(v) => { const n = [...s.projects]; n[i] = { ...p, name: v }; set({ projects: n }); }} placeholder="Project name" />
                    <Field v={p.stack} onChange={(v) => { const n = [...s.projects]; n[i] = { ...p, stack: v }; set({ projects: n }); }} placeholder="Tech stack" />
                  </div>
                  <BulletList bullets={p.bullets} onChange={(b) => { const n = [...s.projects]; n[i] = { ...p, bullets: b }; set({ projects: n }); }} />
                  <button type="button" onClick={() => set({ projects: s.projects.filter((_, j) => j !== i) })} className="text-xs text-red-400 hover:text-red-600 bg-transparent border-0 cursor-pointer mt-1">Remove project</button>
                </div>
              ))}
              <button type="button" onClick={() => set({ projects: [...(s.projects || []), { name: "", stack: "", bullets: [] }] })} className="text-xs text-[#2832C2] hover:underline bg-transparent border-0 cursor-pointer inline-flex items-center gap-1"><Plus className="w-3 h-3" /> project</button>
            </div>
          )}
        </>
      )))}

      {/* Add a removed section back */}
      {hidden.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <span className="text-xs text-gray-400">Add section:</span>
          {hidden.map((c) => (
            <button key={c} type="button" onClick={() => {
              const seed = c === "SUMMARY" ? { summary: " " }
                : c === "EXPERIENCE" ? { experience: [{ company: "", title: "", location: "", dates: "", bullets: [""] }] }
                : c === "EDUCATION" ? { education: [{ institution: "", degree: "", location: "", dates: "", details: "" }] }
                : c === "SKILLS" ? { skills: [] } : c === "TECHNICAL SKILLS" ? { technical_skills: [] }
                : c === "PROJECTS" ? { projects: [{ name: "", stack: "", bullets: [""] }] } : { certifications: [] };
              const ord = [...visible, c, ...CANON.filter((x) => !visible.includes(x) && x !== c)];
              set({ ...seed, section_order: ord });
            }} className="text-xs px-2.5 py-1 rounded-full border border-[#2832C2]/30 text-[#2832C2] hover:bg-[#2832C2]/10 bg-transparent cursor-pointer inline-flex items-center gap-1">
              <Plus className="w-3 h-3" /> {ALIASES[c][0]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
